import EventEmitter from 'eventemitter3';
import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import * as awarenessProtocol from 'y-protocols/awareness';
import type * as Y from 'yjs';

import { type ApplicationMode } from '@canvas/modes/types';
import { MessageType } from '@canvas/protocol';
import { type AwarenessMessage } from '@canvas/protocol-types';
import { type TransactionOrigin, YjsOrigin, YJS_DEFAULT } from '@canvas/transaction';

export enum AwarenessChannel {
  TransientTransaction = 1 << 1,
  Telepointer = 1 << 2,
  Selection = 1 << 3,
  SmartLinks = 1 << 4,
  Participants = 1 << 5,
  Camera = 1 << 6,
}

export interface AwarenessClientIDs {
  added: number[];
  updated: number[];
  removed: number[];
}

interface AwarenessContainerEvents {
  update: (
    channel: AwarenessChannel,
    clientIDs: AwarenessClientIDs,
    origin: unknown
  ) => void;
}

const allAwarenessChannels = Object.keys(AwarenessChannel)
  .map((key) => Number(key))
  .filter((x): x is AwarenessChannel => !isNaN(x));

const allAwarenessChannelsMask = allAwarenessChannels.reduce(
  (mask, channel) => mask | channel,
  0
);

class SingleAwarenessChannel {
  public readonly awareness: awarenessProtocol.Awareness;

  public constructor(
    public readonly channel: AwarenessChannel,
    private emitEvent: EventEmitter<AwarenessContainerEvents>['emit'],
    doc: Y.Doc
  ) {
    this.awareness = new awarenessProtocol.Awareness(doc);
    this.awareness.on('update', this.awarenessUpdateHandler);
  }

  private awarenessUpdateHandler = (clientIDs: AwarenessClientIDs, origin: any) => {
    this.emitEvent('update', this.channel, clientIDs, origin);
  };

  public destroy() {
    this.awareness.off('update', this.awarenessUpdateHandler);
    this.awareness.destroy();
  }
}

/**
 * Contains and manages multiple different awareness channels.
 */
export class AwarenessContainer {
  private readonly transientTransactionAwareness: SingleAwarenessChannel;
  private readonly telepointerAwareness: SingleAwarenessChannel;
  private readonly participantsAwareness: SingleAwarenessChannel;
  private readonly selectionAwareness: SingleAwarenessChannel;
  private readonly smartLinkAwareness: SingleAwarenessChannel;
  private readonly cameraAwareness: SingleAwarenessChannel;

  private readonly allAwareness: SingleAwarenessChannel[];

  private events: EventEmitter<AwarenessContainerEvents> =
    new EventEmitter<AwarenessContainerEvents>();

  public constructor(doc: Y.Doc) {
    const emit = this.events.emit.bind(this.events);
    this.transientTransactionAwareness = new SingleAwarenessChannel(
      AwarenessChannel.TransientTransaction,
      emit,
      doc
    );
    this.telepointerAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Telepointer,
      emit,
      doc
    );
    this.participantsAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Participants,
      emit,
      doc
    );
    this.selectionAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Selection,
      emit,
      doc
    );

    this.smartLinkAwareness = new SingleAwarenessChannel(
      AwarenessChannel.SmartLinks,
      emit,
      doc
    );
    this.cameraAwareness = new SingleAwarenessChannel(AwarenessChannel.Camera, emit, doc);

    this.allAwareness = [
      this.transientTransactionAwareness,
      this.telepointerAwareness,
      this.participantsAwareness,
      this.selectionAwareness,
      this.smartLinkAwareness,
      this.cameraAwareness,
    ];
  }

  public on = this.events.on.bind(this.events);
  public off = this.events.off.bind(this.events);

  /**
   * Returns the awareness object for a given awareness channel.
   */
  public getAwareness(channel: AwarenessChannel) {
    switch (channel) {
      case AwarenessChannel.TransientTransaction:
        return this.transientTransactionAwareness.awareness;
      case AwarenessChannel.Telepointer:
        return this.telepointerAwareness.awareness;
      case AwarenessChannel.Participants:
        return this.participantsAwareness.awareness;
      case AwarenessChannel.Selection:
        return this.selectionAwareness.awareness;
      case AwarenessChannel.SmartLinks:
        return this.smartLinkAwareness.awareness;
      case AwarenessChannel.Camera:
        return this.cameraAwareness.awareness;
    }
  }

  /**
   * Returns the awareness message key for a given awareness channel.
   */
  public getAwarenessMessageKey(
    channel: AwarenessChannel
  ): keyof AwarenessMessage['data'] {
    switch (channel) {
      case AwarenessChannel.TransientTransaction:
        return 'transientTransactionData';
      case AwarenessChannel.Telepointer:
        return 'telepointerData';
      case AwarenessChannel.Participants:
        return 'participantData';
      case AwarenessChannel.Selection:
        return 'selectionData';
      case AwarenessChannel.SmartLinks:
        return 'smartLinksUpdateData';
      case AwarenessChannel.Camera:
        return 'cameraData';
    }
  }

  /**
   * Returns the awareness channel for a given awareness message key.
   */
  public getAwarenessChannel(key: string): AwarenessChannel | null {
    switch (key) {
      case 'transientTransactionData':
        return AwarenessChannel.TransientTransaction;
      case 'telepointerData':
        return AwarenessChannel.Telepointer;
      case 'participantData':
        return AwarenessChannel.Participants;
      case 'selectionData':
        return AwarenessChannel.Selection;
      case 'smartLinksUpdateData':
        return AwarenessChannel.SmartLinks;
      case 'cameraData':
        return AwarenessChannel.Camera;
    }

    return null;
  }

  private filterMessage(
    channel: AwarenessChannel,
    consume: boolean,
    capabilities?: ApplicationMode
  ) {
    if (!capabilities) {
      return true;
    }
    switch (channel) {
      case AwarenessChannel.Selection:
        return consume
          ? capabilities.awareness.selections.canConsume
          : capabilities.awareness.selections.canSend;
      case AwarenessChannel.Telepointer:
        return consume
          ? capabilities.awareness.telepointer.canConsume
          : capabilities.awareness.telepointer.canSend;
      case AwarenessChannel.TransientTransaction:
        return consume
          ? capabilities.awareness.transient.canConsume
          : capabilities.awareness.transient.canSend;
      default:
        return true;
    }
  }

  /**
   * Returns an awareness message for the given awareness channels and client IDs.
   */
  public getAwarenessMessage(
    awarenessChannelClientIdMap: Map<AwarenessChannel, Set<number>>,
    capabilities?: ApplicationMode
  ): AwarenessMessage {
    const message: AwarenessMessage = {
      type: MessageType.Awareness,
      data: {},
    };

    for (const [channel, clientIds] of awarenessChannelClientIdMap) {
      if (!this.filterMessage(channel, false, capabilities)) {
        continue;
      }
      const awareness = this.getAwareness(channel);
      const key = this.getAwarenessMessageKey(channel);

      const encoder = encoding.createEncoder();
      encoding.writeVarUint8Array(
        encoder,
        awarenessProtocol.encodeAwarenessUpdate(
          awareness,
          // clientIds will always only have valid client ID, but filter any invalid ones
          // out just in case
          [...clientIds].filter((clientId) => awareness.meta.has(clientId))
        )
      );

      message.data[key] = encoding.toUint8Array(encoder);
    }
    return message;
  }

  /**
   * Event handler that can be called to propagate an awareness message to all relevant
   * awareness objects.
   */
  public onAwareness = (
    message: AwarenessMessage,
    origin: TransactionOrigin,
    capabilities?: ApplicationMode
  ) => {
    for (const key in message.data) {
      const channel = this.getAwarenessChannel(key);

      if (channel == null) {
        continue;
      }

      if (!this.filterMessage(channel, true, capabilities)) {
        continue;
      }

      const data = message.data[key as keyof AwarenessMessage['data']];
      if (data == null || !(data instanceof Uint8Array)) {
        continue;
      }

      const decoder = decoding.createDecoder(data);

      awarenessProtocol.applyAwarenessUpdate(
        this.getAwareness(channel),
        decoding.readVarUint8Array(decoder),
        new YjsOrigin(YJS_DEFAULT, origin)
      );
    }
  };

  /**
   * Returns the local state of the given awareness channels. Maps each awareness channel
   * to the local client ID, if it has any local state.
   */
  public getLocalState(
    channelMask: AwarenessChannel
  ): Map<AwarenessChannel, Set<number>> | null {
    const result: Map<AwarenessChannel, Set<number>> = new Map();

    for (const channel of allAwarenessChannels) {
      if ((channelMask & channel) === 0) {
        continue;
      }

      const awareness = this.getAwareness(channel);
      if (awareness.getLocalState() !== null) {
        result.set(channel, new Set([awareness.clientID]));
      }
    }

    if (result.size > 0) {
      return result;
    }

    return null;
  }

  /**
   * Returns the local state of all awareness channels. Maps each awareness channel to
   * the local client ID, if it has any local state.
   */
  public getLocalStateForAllChannels(): Map<AwarenessChannel, Set<number>> | null {
    return this.getLocalState(allAwarenessChannelsMask);
  }

  /**
   * Removes all other users from all awareness channels.
   */
  public removeRemoteState(origin: TransactionOrigin) {
    for (const awareness of this.allAwareness) {
      awarenessProtocol.removeAwarenessStates(
        awareness.awareness,
        Array.from(awareness.awareness.getStates().keys()).filter(
          (client) => client !== awareness.awareness.clientID
        ),
        new YjsOrigin(YJS_DEFAULT, origin)
      );
    }
  }

  /**
   * Removes the current user from all awareness channels.
   */
  public removeLocalState(origin: TransactionOrigin) {
    for (const awareness of this.allAwareness) {
      awarenessProtocol.removeAwarenessStates(
        awareness.awareness,
        [awareness.awareness.clientID],
        new YjsOrigin(YJS_DEFAULT, origin)
      );
    }
  }

  public destroy() {
    this.events.removeAllListeners();

    for (const awareness of this.allAwareness) {
      awareness.destroy();
    }
  }
}
